"use client";

import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useState,
} from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useLanguage } from "@/components/LanguageProvider";
import { unwrapApiData } from "@/lib/apiResponse";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { useAppResumeRevision } from "@/lib/useAppResumeRevision";

type UsageDays = 1 | 7 | 30 | 90;
type UsageView = "tokens" | "requests";
type UsageSeriesKey =
  "totalTokens" | "promptTokens" | "completionTokens" | "calls";

type UsageApiResponse = {
  promptTokens?: unknown;
  completionTokens?: unknown;
  totalTokens?: unknown;
  calls?: unknown;
  summary?: unknown;
  daily?: unknown;
  trend?: unknown;
  usage?: unknown;
};

type UsageSummary = {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  calls: number;
};

type UsageTrendPoint = UsageSummary & {
  date: string;
};

type UsageLimits = {
  aiTokensPerMonth: number | null;
  tokensUsedThisMonth: number;
};

type ChartSeries = {
  key: UsageSeriesKey;
  label: string;
  strokeClass: string;
  dotClass: string;
};

type ChartPoint = {
  x: number;
  y: number;
};

const CHART_WIDTH = 640;
const CHART_HEIGHT = 220;
const CHART_TOP = 18;
const CHART_BOTTOM = 28;
const CHART_SIDE = 10;
const TOKEN_COMPACT_UNITS = [
  { threshold: 1_000_000_000_000, suffix: "T" },
  { threshold: 1_000_000_000, suffix: "B" },
  { threshold: 1_000_000, suffix: "M" },
  { threshold: 1_000, suffix: "K" },
] as const;

const API_BASE = () =>
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function toNonNegativeNumber(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function formatCompactTokenCount(value: number): string {
  const safeValue = Number.isFinite(value) && value > 0 ? value : 0;
  const unit = TOKEN_COMPACT_UNITS.find(
    ({ threshold }) => safeValue >= threshold,
  );

  if (!unit) return Math.floor(safeValue).toLocaleString();

  const scaledValue = safeValue / unit.threshold;
  const roundedValue = Math.min(
    Math.round(scaledValue * 1_000) / 1_000,
    scaledValue < 1_000 ? 999.999 : Number.POSITIVE_INFINITY,
  );
  return `${roundedValue.toLocaleString(undefined, {
    maximumFractionDigits: 3,
  })}${unit.suffix}`;
}

function formatSeriesValue(value: number, key: UsageSeriesKey): string {
  return key === "calls"
    ? Math.floor(value).toLocaleString()
    : formatCompactTokenCount(value);
}

function normalizeSummaryRecord(value: unknown): UsageSummary {
  const record = isRecord(value) ? value : {};
  return {
    promptTokens: toNonNegativeNumber(record.promptTokens),
    completionTokens: toNonNegativeNumber(record.completionTokens),
    totalTokens: toNonNegativeNumber(record.totalTokens),
    calls: toNonNegativeNumber(record.calls),
  };
}

export function normalizeUsageSummary(payload: unknown): UsageSummary {
  const data = unwrapApiData<UsageApiResponse>(payload);
  return normalizeSummaryRecord(
    data && isRecord(data.summary) ? data.summary : data,
  );
}

export function normalizeUsageTrend(payload: unknown): UsageTrendPoint[] {
  const data = unwrapApiData<UsageApiResponse>(payload);
  const candidate = data?.daily ?? data?.trend;
  if (!Array.isArray(candidate)) return [];

  return candidate
    .flatMap((value): UsageTrendPoint[] => {
      if (!isRecord(value)) return [];
      const rawDate = value.date ?? value.day ?? value.bucket;
      if (typeof rawDate !== "string" || !rawDate.trim()) return [];
      return [
        {
          date: rawDate,
          ...normalizeSummaryRecord(value),
        },
      ];
    })
    .sort((left, right) => left.date.localeCompare(right.date));
}

function normalizeUsageLimits(payload: unknown): UsageLimits | null {
  const data = unwrapApiData<UsageApiResponse>(payload);
  if (!isRecord(data?.usage)) return null;
  const hasTokenLimit =
    "tokens_limit" in data.usage || "tokensLimit" in data.usage;
  if (!hasTokenLimit) return null;

  const rawLimit =
    "tokens_limit" in data.usage
      ? data.usage.tokens_limit
      : data.usage.tokensLimit;
  return {
    aiTokensPerMonth:
      rawLimit === null ? null : toNonNegativeNumber(rawLimit),
    tokensUsedThisMonth: toNonNegativeNumber(
      data.usage.tokens_used_month ?? data.usage.tokensUsed,
    ),
  };
}

export function UsageDashboard() {
  const { t } = useLanguage();
  const resumeRevision = useAppResumeRevision();
  const [days, setDays] = useState<UsageDays>(30);
  const [view, setView] = useState<UsageView>("tokens");
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [trend, setTrend] = useState<UsageTrendPoint[]>([]);
  const [limits, setLimits] = useState<UsageLimits | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(false);
  const [usageRetryKey, setUsageRetryKey] = useState(0);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [limitsError, setLimitsError] = useState(false);
  const [limitsRetryKey, setLimitsRetryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadUsageLimits() {
      setLimitsLoading(true);
      setLimitsError(false);
      try {
        const response = await fetchWithAuth(
          `${API_BASE()}/api/users/me/plan`,
          { signal: controller.signal },
        );
        if (!response.ok)
          throw new Error(`Usage limits request failed: ${response.status}`);

        const payload: unknown = await response.json();
        const normalizedLimits = normalizeUsageLimits(payload);
        if (!normalizedLimits)
          throw new Error("Usage limits response is missing limits");

        if (!controller.signal.aborted) setLimits(normalizedLimits);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        console.warn("[settings] usage limits request failed", loadError);
        setLimitsError(true);
      } finally {
        if (!controller.signal.aborted) setLimitsLoading(false);
      }
    }

    void loadUsageLimits();
    return () => controller.abort();
  }, [limitsRetryKey, resumeRevision]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadUsage() {
      setUsageLoading(true);
      setUsageError(false);
      setSummary(null);
      setTrend([]);
      try {
        const response = await fetchWithAuth(
          `${API_BASE()}/api/usage/me?days=${days}`,
          { signal: controller.signal },
        );
        if (!response.ok)
          throw new Error(`Usage request failed: ${response.status}`);

        const payload: unknown = await response.json();
        if (!controller.signal.aborted) {
          setSummary(normalizeUsageSummary(payload));
          setTrend(normalizeUsageTrend(payload));
        }
      } catch (loadError) {
        if (controller.signal.aborted) return;
        console.warn("[settings] usage request failed", loadError);
        setUsageError(true);
      } finally {
        if (!controller.signal.aborted) setUsageLoading(false);
      }
    }

    void loadUsage();
    return () => controller.abort();
  }, [days, resumeRevision, usageRetryKey]);

  const rangeOptions: { value: UsageDays; label: string }[] = [
    { value: 1, label: t("usage.range.1d") },
    { value: 7, label: t("usage.range.7d") },
    { value: 30, label: t("usage.range.30d") },
    { value: 90, label: t("usage.range.90d") },
  ];
  const viewOptions: { value: UsageView; label: string }[] = [
    { value: "tokens", label: t("usage.view.tokens") },
    { value: "requests", label: t("usage.view.requests") },
  ];
  const chartSeries: ChartSeries[] =
    view === "tokens"
      ? [
          {
            key: "totalTokens",
            label: t("usage.total_tokens"),
            strokeClass: "stroke-seexr-primary",
            dotClass: "bg-seexr-solid",
          },
          {
            key: "promptTokens",
            label: t("usage.prompt_tokens"),
            strokeClass: "stroke-[#d97706] dark:stroke-[#fbbf24]",
            dotClass: "bg-[#d97706] dark:bg-[#fbbf24]",
          },
          {
            key: "completionTokens",
            label: t("usage.completion_tokens"),
            strokeClass: "stroke-[#e11d48] dark:stroke-[#fb7185]",
            dotClass: "bg-[#e11d48] dark:bg-[#fb7185]",
          },
        ]
      : [
          {
            key: "calls",
            label: t("usage.requests"),
            strokeClass: "stroke-[#10b981]",
            dotClass: "bg-[#10b981]",
          },
        ];

  return (
    <div>
      <div className="mb-4 border-b border-gray-200 pb-4 dark:border-[#2d2d30]">
        <h2 className="text-[16px] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
          {t("usage.title")}
        </h2>
        <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
          {t("usage.subtitle")}
        </p>
      </div>

      <PlanLimitOverview
        limits={limits}
        loading={limitsLoading}
        error={limitsError}
        onRetry={() => setLimitsRetryKey((current) => current + 1)}
      />

      <section aria-labelledby="usage-trend-title" className="mt-6">
        <div>
          <h3
            id="usage-trend-title"
            className="text-[13px] font-semibold text-gray-900 dark:text-gray-100"
          >
            {t("usage.trend_title")}
          </h3>
          <p className="mt-0.5 text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
            {t("usage.trend_desc")}
          </p>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl
            label={t("usage.period")}
            options={rangeOptions}
            value={days}
            onChange={setDays}
          />
          <SegmentedControl
            label={t("usage.view.label")}
            options={viewOptions}
            value={view}
            onChange={setView}
          />
        </div>

        {usageError ? (
          <UsageLoadError
            onRetry={() => setUsageRetryKey((current) => current + 1)}
          />
        ) : (
          <>
            <UsageLegend
              loading={usageLoading}
              series={chartSeries}
              summary={summary}
            />
            <UsageLineChart
              loading={usageLoading}
              points={trend}
              series={chartSeries}
              total={
                view === "tokens"
                  ? (summary?.totalTokens ?? 0)
                  : (summary?.calls ?? 0)
              }
            />
          </>
        )}
      </section>
    </div>
  );
}

function PlanLimitOverview({
  limits,
  loading,
  error,
  onRetry,
}: {
  limits: UsageLimits | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const { t } = useLanguage();

  return (
    <section
      aria-labelledby="usage-limit-title"
      className="rounded-xl bg-gray-100/80 px-4 py-4 dark:bg-white/[0.06]"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3
            id="usage-limit-title"
            className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100"
          >
            {t("usage.limit_title")}
          </h3>
          <p className="mt-0.5 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
            {t("usage.limit_desc")}
          </p>
        </div>
        {!loading && !error && limits?.aiTokensPerMonth === null && (
          <span className="rounded-md bg-white px-2 py-0.5 text-[9px] font-semibold text-gray-500 dark:bg-white/10 dark:text-gray-400">
            무제한
          </span>
        )}
      </div>

      <div className="mt-4 divide-y divide-gray-200 dark:divide-[#303034]">
        {error ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
              {t("usage.error")}
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-[#414146] dark:bg-[#242427] dark:text-gray-300 dark:hover:bg-[#2a2a2d]"
            >
              <RefreshCw size={11} strokeWidth={2} />
              {t("settings.retry")}
            </button>
          </div>
        ) : (
          <LimitBar
            label={t("usage.monthly_limit")}
            used={limits?.tokensUsedThisMonth ?? 0}
            limit={limits?.aiTokensPerMonth ?? null}
            loading={loading}
          />
        )}
      </div>
    </section>
  );
}

function LimitBar({
  label,
  used,
  limit,
  loading,
}: {
  label: string;
  used: number;
  limit: number | null;
  loading: boolean;
}) {
  const { t } = useLanguage();
  const percent =
    limit === null || limit <= 0
      ? 0
      : Math.min(100, Math.round((used / limit) * 100));

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10.5px] font-medium text-gray-700 dark:text-gray-300">
          {label}
        </p>
        <p className="text-[10px] font-medium text-gray-400 tabular-nums dark:text-gray-500">
          {loading
            ? "—"
            : `${formatCompactTokenCount(used)} / ${limit === null ? "∞" : formatCompactTokenCount(limit)}`} {t("usage.token_unit")}
        </p>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10"
      >
        <div
          className="h-full rounded-full bg-seexr-solid transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[9px] font-medium text-gray-400 dark:text-gray-500">
        <span>{limit === null ? "플랜 무제한" : "이번 달 사용량"}</span>
        <span>{limit === null ? "∞" : `${percent}%`}</span>
      </div>
    </div>
  );
}

function SegmentedControl<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex w-fit items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-white/[0.07]"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={`rounded-md px-2.5 py-1.5 text-[10.5px] font-medium transition-all ${
            value === option.value
              ? "bg-white text-gray-950 shadow-sm dark:bg-[#343438] dark:text-gray-100"
              : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function UsageLegend({
  loading,
  series,
  summary,
}: {
  loading: boolean;
  series: ChartSeries[];
  summary: UsageSummary | null;
}) {
  return (
    <dl
      className={`mt-4 grid divide-x divide-gray-200 dark:divide-[#2d2d30] ${
        series.length === 1 ? "grid-cols-1" : "grid-cols-3"
      }`}
    >
      {series.map((item) => (
        <div key={item.key} className="min-w-0 px-3 first:pl-0 last:pr-0">
          <dt className="flex items-center gap-1.5 text-[9.5px] font-medium text-gray-500 dark:text-gray-400">
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.dotClass}`}
            />
            <span className="truncate">{item.label}</span>
          </dt>
          {loading ? (
            <dd className="mt-1.5 h-4 w-16 animate-pulse rounded bg-gray-100 dark:bg-white/5" />
          ) : (
            <dd className="mt-1 text-[13px] font-semibold text-gray-950 tabular-nums dark:text-gray-50">
              {formatSeriesValue(summary?.[item.key] ?? 0, item.key)}
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}

function UsageLineChart({
  loading,
  points,
  series,
  total,
}: {
  loading: boolean;
  points: UsageTrendPoint[];
  series: ChartSeries[];
  total: number;
}) {
  const { t, language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeSeriesKey, setActiveSeriesKey] = useState<UsageSeriesKey | null>(
    null,
  );
  const plotHeight = CHART_HEIGHT - CHART_TOP - CHART_BOTTOM;
  const plotWidth = CHART_WIDTH - CHART_SIDE * 2;
  const isRequestView = series.length === 1 && series[0].key === "calls";
  const barWidth = Math.max(
    2,
    Math.min(22, (plotWidth / Math.max(points.length, 1)) * 0.62),
  );
  const maxValue = Math.max(
    1,
    ...points.flatMap((point) =>
      series.map((item) => point[item.key] as number),
    ),
  );
  const xForIndex = (index: number) =>
    isRequestView
      ? CHART_SIDE + ((index + 0.5) / Math.max(points.length, 1)) * plotWidth
      : points.length <= 1
        ? CHART_WIDTH / 2
        : CHART_SIDE + (index / (points.length - 1)) * plotWidth;
  const yForValue = (value: number) =>
    CHART_TOP + (1 - value / maxValue) * plotHeight;
  const activePoint =
    activeIndex === null ? null : (points[activeIndex] ?? null);
  const activeSeries =
    series.find((item) => item.key === activeSeriesKey) ?? series[0];

  function handlePointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    if (points.length === 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX =
      ((event.clientX - bounds.left) / bounds.width) * CHART_WIDTH;
    const pointerY =
      ((event.clientY - bounds.top) / bounds.height) * CHART_HEIGHT;
    const index =
      points.length === 1
        ? 0
        : isRequestView
          ? Math.floor(((pointerX - CHART_SIDE) / plotWidth) * points.length)
          : Math.round(
              ((pointerX - CHART_SIDE) / plotWidth) * (points.length - 1),
            );
    const safeIndex = Math.max(0, Math.min(points.length - 1, index));
    const point = points[safeIndex];
    const nearestSeries = series.reduce((nearest, item) => {
      const distance = Math.abs(yForValue(point[item.key]) - pointerY);
      const nearestDistance = Math.abs(
        yForValue(point[nearest.key]) - pointerY,
      );
      return distance < nearestDistance ? item : nearest;
    }, series[0]);

    setActiveIndex(safeIndex);
    setActiveSeriesKey(nearestSeries.key);
  }

  const labelIndexes =
    points.length > 0
      ? Array.from(
          new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]),
        )
      : [];

  return (
    <div className="relative mt-3 h-[220px] rounded-xl border border-gray-200 bg-white dark:border-[#303034] dark:bg-black/10">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        onPointerMove={handlePointerMove}
        onPointerLeave={() => {
          setActiveIndex(null);
          setActiveSeriesKey(null);
        }}
      >
        {[0, 1, 2, 3].map((line) => {
          const y = CHART_TOP + (line / 3) * plotHeight;
          return (
            <line
              key={line}
              x1={CHART_SIDE}
              x2={CHART_WIDTH - CHART_SIDE}
              y1={y}
              y2={y}
              className="stroke-gray-100 dark:stroke-[#29292c]"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

        {points.length > 0 &&
          (isRequestView
            ? points.map((point, index) => {
                const valueY = yForValue(point.calls);
                const chartBottom = CHART_HEIGHT - CHART_BOTTOM;
                const height = Math.max(1, chartBottom - valueY);
                const active = activeIndex === index;
                const dimmed = activeIndex !== null && !active;
                return (
                  <rect
                    key={point.date}
                    x={xForIndex(index) - barWidth / 2}
                    y={chartBottom - height}
                    width={barWidth}
                    height={height}
                    rx={Math.min(4, barWidth / 2)}
                    className={`fill-[#10b981] transition-opacity duration-150 ${
                      dimmed
                        ? "opacity-30"
                        : active
                          ? "opacity-100"
                          : "opacity-75"
                    }`}
                  />
                );
              })
            : series.map((item) => {
                const chartPoints = points.map((point, index) => ({
                  x: xForIndex(index),
                  y: yForValue(point[item.key]),
                }));
                const dimmed =
                  activeSeriesKey !== null && activeSeriesKey !== item.key;
                return (
                  <path
                    key={item.key}
                    d={buildSmoothPath(chartPoints)}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`${item.strokeClass} transition-opacity ${
                      dimmed ? "opacity-20" : "opacity-100"
                    }`}
                    strokeWidth={
                      activeSeriesKey === item.key || activeSeriesKey === null
                        ? "2.5"
                        : "1.5"
                    }
                    vectorEffect="non-scaling-stroke"
                  />
                );
              }))}

        {activePoint && activeIndex !== null && (
          <>
            <line
              x1={xForIndex(activeIndex)}
              x2={xForIndex(activeIndex)}
              y1={CHART_TOP}
              y2={CHART_HEIGHT - CHART_BOTTOM}
              className="stroke-gray-300 dark:stroke-gray-600"
              strokeDasharray="3 4"
              vectorEffect="non-scaling-stroke"
            />
            {!isRequestView &&
              series.map((item) => (
                <circle
                  key={item.key}
                  cx={xForIndex(activeIndex)}
                  cy={yForValue(activePoint[item.key])}
                  r={activeSeriesKey === item.key ? 5 : 3.5}
                  className={`${item.strokeClass} fill-white dark:fill-[#171719]`}
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
          </>
        )}

        {labelIndexes.map((index) => (
          <text
            key={index}
            x={xForIndex(index)}
            y={CHART_HEIGHT - 8}
            textAnchor={
              index === 0
                ? "start"
                : index === points.length - 1
                  ? "end"
                  : "middle"
            }
            className="fill-gray-400 text-[9px] dark:fill-gray-500"
          >
            {formatTrendDate(points[index].date, language)}
          </text>
        ))}
      </svg>

      {points.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
          {loading ? (
            <>
              <div className="h-2 w-40 animate-pulse rounded-full bg-gray-100 dark:bg-white/5" />
              <p className="mt-3 text-[10px] font-medium text-gray-400 dark:text-gray-500">
                {t("usage.loading")}
              </p>
            </>
          ) : (
            <>
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                {total === 0
                  ? t("usage.chart.empty")
                  : t("usage.trend_pending")}
              </p>
              {total > 0 && (
                <p className="mt-1 max-w-[290px] text-[9.5px] leading-relaxed text-gray-400 dark:text-gray-500">
                  {t("usage.trend_pending_desc")}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {activePoint && activeIndex !== null && (
        <div
          className="pointer-events-none absolute z-10 min-w-[150px] -translate-x-1/2 -translate-y-full rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-[0_8px_24px_rgba(15,23,42,0.14)] dark:border-[#3a3a3e] dark:bg-[#242427]"
          style={{
            left: `${Math.max(
              18,
              Math.min(82, (xForIndex(activeIndex) / CHART_WIDTH) * 100),
            )}%`,
            top: `${Math.max(
              28,
              (yForValue(activePoint[activeSeries.key]) / CHART_HEIGHT) * 100,
            )}%`,
          }}
        >
          <p className="text-[9.5px] font-semibold text-gray-500 dark:text-gray-400">
            {formatTrendDate(activePoint.date, language)}
          </p>
          <div className="mt-1.5 space-y-1">
            {series.map((item) => (
              <div
                key={item.key}
                className={`flex items-center justify-between gap-4 text-[10px] ${
                  activeSeriesKey === item.key
                    ? "font-semibold text-gray-950 dark:text-gray-50"
                    : "font-medium text-gray-500 dark:text-gray-400"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 rounded-full ${item.dotClass}`}
                  />
                  {item.label}
                </span>
                <span className="tabular-nums">
                  {formatSeriesValue(activePoint[item.key], item.key)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsageLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useLanguage();

  return (
    <div className="mt-4 rounded-xl bg-gray-100/80 p-4 dark:bg-white/[0.06]">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-red-500 shadow-sm dark:bg-[#2a2a2d] dark:text-red-400">
          <AlertCircle size={17} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100">
            {t("usage.error")}
          </h3>
          <p className="mt-1 text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
            {t("usage.error_desc")}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-[#414146] dark:bg-[#242427] dark:text-gray-200 dark:hover:bg-[#2a2a2d]"
          >
            <RefreshCw size={12} strokeWidth={2} />
            {t("settings.retry")}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTrendDate(value: string, locale: string): string {
  const includesHour = value.includes("T");
  const date = new Date(includesHour ? `${value}Z` : `${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    ...(includesHour ? { hour: "numeric" } : {}),
  }).format(date);
}

function buildSmoothPath(points: ChartPoint[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  const tension = 0.62;
  const clampY = (value: number) =>
    Math.max(CHART_TOP, Math.min(CHART_HEIGHT - CHART_BOTTOM, value));
  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[index - 1] ?? points[index];
    const current = points[index];
    const next = points[index + 1];
    const afterNext = points[index + 2] ?? next;
    const firstControlX = current.x + ((next.x - previous.x) / 6) * tension;
    const firstControlY = clampY(
      current.y + ((next.y - previous.y) / 6) * tension,
    );
    const secondControlX = next.x - ((afterNext.x - current.x) / 6) * tension;
    const secondControlY = clampY(
      next.y - ((afterNext.y - current.y) / 6) * tension,
    );

    path += ` C ${firstControlX} ${firstControlY}, ${secondControlX} ${secondControlY}, ${next.x} ${next.y}`;
  }

  return path;
}
